import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { login } from "../../Services/loginService";
import * as Utils from "../../Utils";
import { UserContext } from "../../Context";
import { HOME, SIGNUP } from "../../routes";
import { MESSAGES } from "./Constants";
import { PinInput } from "../../Components/Common";
import { silverline_image, SilverLine_Logo, loginpage } from "../../Assets";
import { COMPANY_NAME } from "../../Constants/Strings";

const LoginForm = () => {
  const [error, setError] = useState("");
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const userSchema = Yup.object({
    mobile: Yup.string()
      .matches(/^\d{10}$/, MESSAGES.MOBILE_VALIDATION)
      .required(MESSAGES.REQUIRED),
    password: Yup.string()
      .matches(/^\d{4}$/, MESSAGES.PIN_VALIDATION)
      .required(MESSAGES.REQUIRED),
  });

  const clearForm = (values) => {
    values.mobile = "";
    values.password = "";
  };

  const onSubmit = (values, { setSubmitting }) => {
    login(values)
      .then((response) => {
        if (response.status === 200 && response.data) {
          Utils.setUser(response.data);
          setUser(response.data);
          navigate(HOME, { replace: true });
        } else if (response.error) {
          const errorMsg = response.error.errors.error[0];
          setError(errorMsg);
          clearForm(values);
          toast.error(MESSAGES.LOGIN_FAILURE);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="flex min-h-screen">
      {/* Left image section for large screens */}
      <div className="w-3/5 hidden lg:block">
        <img
          className="w-full h-screen object-cover"
          src={loginpage}
          alt="background"
        />
      </div>

      {/* Right form section */}
      <div className="relative w-full lg:w-2/5 flex items-center justify-center">
        {/* Mobile-only background */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-80 z-0 block lg:hidden"
          style={{ backgroundImage: `url(${loginpage})` }}
        ></div>

        {/* Login form container */}
        <div className="relative z-10 flex flex-col gap-4 w-full max-w-md m-4 p-6 bg-white/90 md:bg-white shadow-md rounded-lg border border-gray">
          {/* Logo & Company Name */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-4">
              <img
                src={SilverLine_Logo}
                alt="Logo"
                className="w-10 h-10 object-contain opacity-80"
              />
              <p className="text-3xl text-primary font-semibold">
                {COMPANY_NAME}
              </p>
            </div>
            <p className="text-center text-2xl text-primary mt-4 font-semibold">
              LOGIN
            </p>
          </div>

          {/* Form */}
          <Formik
            initialValues={{ mobile: "", password: "" }}
            validationSchema={userSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <Form
                className="flex flex-col gap-4 w-full text-primary"
                autoComplete="off"
              >
                {/* Mobile Number Field */}
                <div className="w-full font-semibold">
                  <label className="text-gray-600">Mobile</label>
                  <Field name="mobile">
                    {({ field }) => (
                      <input
                        {...field}
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter your mobile number"
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                        className="w-full py-3 px-3 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-primary"
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="mobile"
                    component="p"
                    className="text-red-500 text-xs italic"
                  />
                </div>

                {/* PIN Input Field */}
                <PinInput name="password" label="PIN" />

                {/* Error Message */}
                {error && (
                  <p className="text-red-500 italic text-center text-sm">
                    {error}
                  </p>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-hoverColor text-white p-2 rounded focus:outline-none"
                >
                  Log In
                </button>

                {/* Signup Link */}
                <div className="flex items-center gap-2 justify-center text-center mt-2 text-sm text-gray-600">
                  <span>Don't have an account?</span>
                  <Link
                    to={SIGNUP}
                    className="font-semibold text-gray-600 hover:underline"
                  >
                    Create an Account!
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
